/**
 * Transport-blind primitives shared by the realtime job-ready clients and the
 * polling fast path: the {@link JobReady} envelope, the two fallback-signalling
 * error types, and the idle-close delay. Kept in a neutral module so neither the
 * client nor the fast-path wrapper owns them.
 */

export interface JobReady {
  key: string;
  payload: unknown;
}

/** Idle period (ms) after the last resolver is removed before the connection is closed. Exported for tests. */
export const IDLE_CLOSE_DELAY_MS = 2 * 60 * 1000;

/**
 * Thrown by a realtime client's `awaitJob` when the one-shot immediate status
 * read rejects. That rejection means the JOB itself errored (the status endpoint
 * returned an error response) — NOT that the realtime connection/subscription
 * failed. The caller uses this to fall back to the legacy loop (which re-fetches
 * and surfaces the job error) WITHOUT logging a transport-health fallback, keeping
 * `reason=error` reserved for genuine transport failures.
 */
export class ImmediateCheckError extends Error {
  readonly reason: unknown;

  constructor(reason: unknown) {
    super('stream-transport: immediate status check rejected');
    this.name = 'ImmediateCheckError';
    this.reason = reason;
  }
}

/**
 * Thrown by a realtime client's `awaitJob` when the server rejects the
 * subscription. Distinct from a connect failure: the connection itself is up,
 * only the subscription was refused — the caller logs it under its own telemetry
 * reason so the two failure modes are separable in production.
 */
export class SubscribeRejectedError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`stream-transport: subscribe rejected with status ${status}`);
    this.name = 'SubscribeRejectedError';
    this.status = status;
  }
}
